import { uiPaper } from "shared/ui/uiSurface"
import {
  Cancel,
  CheckCircle,
  Close,
  HourglassEmpty,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"

export interface BatchAssignmentOrder {
  orderId: number
  reference: string
  status: "pending" | "success" | "error"
  error?: string
}

interface AssignmentProgressDialogProps {
  open: boolean
  employeeName: string
  orders: BatchAssignmentOrder[]
  onClose: () => void
}

export const AssignmentProgressDialog: FC<AssignmentProgressDialogProps> = ({
  open,
  employeeName,
  orders,
  onClose,
}) => {
  const { t } = useTranslation()

  const total = orders.length
  const completed = orders.filter(
    (o) => o.status === "success" || o.status === "error",
  ).length
  const successCount = orders.filter((o) => o.status === "success").length
  const errorCount = orders.filter((o) => o.status === "error").length
  const isDone = completed === total && total > 0
  const progress = total > 0 ? (completed / total) * 100 : 0

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DSP-035") }}
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!isDone}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" && !isDone) return
        if (isDone) onClose()
      }}
    >
      <DialogTitle>
        {t("batchAssignTitle", {
          count: total,
          employeeName,
        })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
            {isDone
              ? t("batchAssignComplete", {
                  success: successCount,
                  failed: errorCount,
                  total,
                })
              : t("batchAssignProcessing", { completed, total })}
          </Typography>
        </Box>

        <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
          {orders.map((order) => (
            <ListItem key={order.orderId} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {order.status === "success" && (
                  <CheckCircle color="success" fontSize="small" />
                )}
                {order.status === "error" && (
                  <Cancel color="error" fontSize="small" />
                )}
                {order.status === "pending" && (
                  <HourglassEmpty color="disabled" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={order.reference}
                secondary={order.status === "error" ? order.error : undefined}
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{
                  variant: "caption",
                  color: "error",
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={!isDone}
          startIcon={<Close />}
          variant="contained"
          size="small"
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
